import React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
  KambiBetStatusQuery,
  getKambiBetsHistory,
} from '@core/Areas/KambiSports/Player/helpers/api'
import { AutoFetchButton } from '@core/Components/AutoFetchButton'
import { queryKeys } from '@core/Data/ReactQuery/constants'

const RANGE_SIZE = 30

export const useGetPlayerKambiBetsHistoryInfiniteQuery = (
  betStatus: KambiBetStatusQuery,
  enabled: boolean
) => {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, isError } =
    useInfiniteQuery({
      queryKey: [...queryKeys.kambiPlayerBetHistory, 'my-bet', betStatus.toLowerCase()],
      queryFn: async ({ pageParam = 0 }) => {
        return await getKambiBetsHistory(betStatus, RANGE_SIZE, pageParam)
      },
      initialPageParam: 0,
      getNextPageParam: lastPage => {
        if (!lastPage || !lastPage.range.more) return undefined
        return lastPage.range.start + RANGE_SIZE
      },
      enabled,
    })

  const isFinalPage =
    data && data.pages.length > 0 && !data.pages[data.pages.length - 1]?.range.more

  const kambiHistoryData = data?.pages
    .flatMap(k => k?.historyCoupons)
    .filter(historyCoupon => historyCoupon != undefined)
    .sort((a, b) => {
      const earliestEventTimeA = a.events.reduce(
        (earliest, e) => (e.eventStartDate < earliest ? e.eventStartDate : earliest),
        a.events[0].eventStartDate
      )
      const earliestEventTimeB = b.events.reduce(
        (earliest, e) => (e.eventStartDate < earliest ? e.eventStartDate : earliest),
        b.events[0].eventStartDate
      )
      return new Date(earliestEventTimeB).getTime() - new Date(earliestEventTimeA).getTime()
    })
  const isEmptyState = !isLoading && !isError && (kambiHistoryData?.length ?? 0) === 0

  return {
    kambiHistoryData,
    isLoading,
    isFinalPage,
    hasNextPage,
    refetch,
    isError,
    isEmptyState,
    LoadMoreButton: () => (
      <AutoFetchButton onClick={fetchNextPage} isFetching={isFetchingNextPage} />
    ),
    fetchNextPage,
    isFetchingNextPage,
  }
}
